import { dependencies } from '@pn/core/dependencies';
import type { Layer } from '@pn/core/domain/layer';
import type { UserSettings } from '@pn/core/domain/settings';
import type { User, UserForm } from '@pn/core/domain/user';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { isNil } from 'lodash-es';
import { currentUserSlice } from './userSlice';

export const useCurrentUserStorage = () => {
  const { name, getInitialState } = currentUserSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isError: useSliceSelector((state) => state.isError),
    user: useSliceSelector((state) => state.user),
    isUserLoaded: useSliceSelector((state) => !isNil(state.user)),
    isStackUser: useSliceSelector(
      (state) => !isNil(state.user?.stackCompanyId)
    ),
    stackCompanyId: useSliceSelector((state) => state.user?.stackCompanyId),
    settings: useSliceSelector((state) => state.settings),
    isAdminMode: useSliceSelector(
      (state) => state.isAdminMode && state.user?.isAdmin === true
    ),
  };
};

export const currentUserActions = () => {
  const { dispatch } = dependencies.store;
  const { actions } = currentUserSlice;

  return {
    request: () => dispatch(actions.request()),
    receive: (user: User, settings: UserSettings) =>
      dispatch(actions.receive({ user, settings })),
    error: () => dispatch(actions.error()),
    updateProfile: (userForm: UserForm) =>
      dispatch(actions.updateProfile(userForm)),
    updateStackCompanyId: (stackCompanyId: string) =>
      dispatch(actions.updateStackCompanyId(stackCompanyId)),
    updateChartConfig: (config: Partial<UserSettings['chartConfig']>) =>
      dispatch(actions.updateChartConfig(config)),
    updateGrids: (grids: Partial<UserSettings['grids']>) =>
      dispatch(actions.updateGrids(grids)),
    updateBaseLayers: (baseLayers: Partial<UserSettings['baseLayers']>) =>
      dispatch(actions.updateBaseLayers(baseLayers)),
    updatePnLayers: (itemId: string, layers: Layer[]) =>
      dispatch(actions.updatePnLayers({ itemId, layers })),
    removePnLayers: (itemId: string) =>
      dispatch(actions.removePnLayers(itemId)),
    toggleAdminMode: () => dispatch(actions.toggleAdminMode()),
  };
};
